import { State, Selector, Select, Action, StateContext, Store } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { ParcsState, ParcsStateDefault } from './parcs.state';
import { ParcsService, MaterielRecyparcsService, FermeturesExceptionnellesRecyparcsService, FermeturesTemporairesRecyparcsService, ParcsLimitrophesRecyparcsService, HeuresOuvertureRecyparcsService, TypesDechetsRecyparcsService, TelephonesRecyparcsService, AlertesService, TypesEvacuationRecyparcsService, PhotosPdaService } from 'src/api/services';
import { Parcs } from './parcs.actions';
import { HeureOuvertureDto, ParcDetailsDto, ParcLimitropheDto, ParcLimitropheForConfigurationDto, FermetureTemporaireDto, ParcActifDto, AlerteDto, TypeDechetParcDto, TypeEvacuationDto } from 'src/api/models';
import { OuvertureParc } from 'src/core/models/OuvertureParc';
import * as moment from 'moment';

@State<ParcsState>({
  name: 'parcs',
  defaults: ParcsStateDefault,
})
@Injectable()
export class ParcsStore {

  constructor(private parcService: ParcsService,
    private typesDechetsRecyparcsService: TypesDechetsRecyparcsService,
    private typesEvacuationRecyparcsService: TypesEvacuationRecyparcsService,
    private materielService: MaterielRecyparcsService,
    private telephoneService: TelephonesRecyparcsService,
    private fermetureExceptionnelleService: FermeturesExceptionnellesRecyparcsService,
    private fermetureTemporaireService : FermeturesTemporairesRecyparcsService,
    private parcLimitropheService: ParcsLimitrophesRecyparcsService,
    private heuresOuvertureService: HeuresOuvertureRecyparcsService,
    private photosPdaService: PhotosPdaService,
    private alertesService: AlertesService) { }

  @Selector()
  static parcsActifs$(state: ParcsState) {
    return state.parcsActifs;
  }

  @Selector()
  static parc$(state: ParcsState) {
    return state.parc;
  }

  @Selector()
  static visitesDuJour$(state:ParcsState) {
    return state.visitesDuJour;
  }

  @Selector()
  static error$(state: ParcsState) {
    return state.error;
  }

  @Selector()
  static parcsLimitrophes$(state: ParcsState) {
    return state.parcsLimitrophes;
  }

  @Selector()
  static heuresOuverture$(state: ParcsState) {
    return state.heuresOuverture;
  }

  @Selector()
  static typesDechetsParcNoDestination$(state: ParcsState) {
    return state.typesDechetsParcNoDestination;
  }

  @Selector()
  static typesEvacuationParcNoDestination$(state: ParcsState) {
    return state.typesEvacuationParcNoDestination;
  }

  @Action(Parcs.GetActifs)
  getActive( { patchState }: StateContext<ParcsState>) {
    return this.parcService.parcGetListActifs().pipe(
      tap( (res: ParcActifDto[]) => {
        patchState({
          parcsActifs: res,
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          parcsActifs: null,
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.GetDetail)
  getDetail( { patchState }: StateContext<ParcsState>, { parcId }: Parcs.GetDetail) {
    return this.parcService.parcGetDetail({ id: parcId }).pipe(
      tap( (res: ParcDetailsDto) => {
        patchState({
          parc: res
        });
      }),
      catchError( (error: Error) => {
        patchState({
          parc: null,
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.GetDailyVisits)
  getDailyVisits( { patchState }: StateContext<ParcsState>, { parcId } : Parcs.GetDailyVisits) {
    return this.parcService.parcGetTodayVisitesCount( { parcId: parcId}).pipe(
      tap( (res: number) => {
        patchState({
          visitesDuJour: res
        });
      }),
      catchError( (error: Error) => {
        patchState({
          visitesDuJour: null,
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.UpdateDetail)
  updateDetail( { patchState }: StateContext<ParcsState>, { parc }: Parcs.UpdateDetail) {
    return this.parcService.parcPut({ id: parc.Id, body: parc }).pipe(
      tap( (res: ParcDetailsDto) => {
        patchState({
          error: null,
          parc: res
        });
      }),
      catchError( (error) => {
        const errorMessage = error.error ?? "";
        patchState({
          parc: null,
          error: errorMessage
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.UpdateTypeDechetParc)
  updateTypeDechetParc( { patchState }: StateContext<ParcsState>, { parcId, typeDechetsParc }: Parcs.UpdateTypeDechetParc) {
    return this.typesDechetsRecyparcsService.typeDechetParcPutMultiple({ parcId, body: typeDechetsParc }).pipe(
      tap( (res: ParcDetailsDto[]) => {
        patchState({
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.GetTypeDechetParc)
  getTypeDechetParc( { patchState }: StateContext<ParcsState>, { parcId }: Parcs.GetTypeDechetParc) {
    return this.typesDechetsRecyparcsService.typeDechetParcGetListActifsNoDestination({ parcId }).pipe(
      tap( (res: TypeDechetParcDto[]) => {
        patchState({
          typesDechetsParcNoDestination: res,
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.GetTypeEvacuationParc)
  getTypeEvacuationParc( { patchState }: StateContext<ParcsState>, { parcId }: Parcs.GetTypeEvacuationParc) {
    return this.typesEvacuationRecyparcsService.typeEvacuationParcGetListNoDestination({ parcId }).pipe(
      tap( (res: TypeEvacuationDto[]) => {
        patchState({
          typesEvacuationParcNoDestination: res,
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.Desactivate)
  desactivate( { patchState }: StateContext<ParcsState>, { parc }: Parcs.Desactivate) {
    return this.parcService.parcDesactivate({ id: parc.Id, body: parc }).pipe(
      tap( (res: number) => {
        patchState({
          parc: null,
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          parc: null,
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.DeleteMateriel)
  deleteMateriel( { patchState }: StateContext<ParcsState>, { materiel }: Parcs.DeleteMateriel) {
    return this.materielService.materielParcDelete({ id: materiel.Id }).pipe(
      tap( (res: number) => {
        patchState({
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.DeleteTelephone)
  deleteTelephone({ patchState }: StateContext<ParcsState>, { telephone }: Parcs.DeleteTelephone) {
    return this.telephoneService.telephoneParcDelete({ id: telephone.Id }).pipe(
      tap((res: number) => {
        patchState({
          error: null
        });
      }),
      catchError((error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.DeleteFermetureExceptionnelle)
  deleteFermetureExceptionnelle( { patchState }: StateContext<ParcsState>, { fermeture }: Parcs.DeleteFermetureExceptionnelle) {
    return this.fermetureExceptionnelleService.fermetureExceptionnelleDelete({ id: fermeture.Id }).pipe(
      tap( (res: number) => {
        patchState({
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.AddFermetureTemporaire)
  addFermetureTemporaire( { patchState }: StateContext<ParcsState>, { parcId, fermeture }: Parcs.AddFermetureTemporaire) {
    return this.fermetureTemporaireService.fermetureTemporairePost({ parcId: parcId, body: fermeture }).pipe(
      tap( (res: FermetureTemporaireDto) => {
        patchState({
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.DeleteFermetureTemporaire)
  deleteFermetureTemporaire( { patchState }: StateContext<ParcsState>, { fermeture }: Parcs.DeleteFermetureTemporaire) {
    return this.fermetureTemporaireService.fermetureTemporaireDelete({ id: fermeture.Id }).pipe(
      tap( (res: number) => {
        patchState({
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.AddParcLimitrophe)
  addParcLimitrophe( { patchState }: StateContext<ParcsState>, { parcId, parc }: Parcs.AddParcLimitrophe) {
    return this.parcLimitropheService.parcLimitrophePost( { parcId: parcId, body: parc }).pipe(
      tap( (res: ParcLimitropheDto) => {
        patchState({
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.FetchParcsLimitrophesForConfiguration)
  fetchParcsLimitropheForConfiguration( {patchState }: StateContext<ParcsState>, { parcId }: Parcs.FetchParcsLimitrophesForConfiguration) {
    return this.parcLimitropheService.parcLimitropheGetListForUpdate( { parcId: parcId }).pipe(
      tap( (res: ParcLimitropheForConfigurationDto[]) => {
        patchState({
          parcsLimitrophes: res
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.DeleteParcLimitrophe)
  deleteParcLimitrophe( { patchState }:StateContext<ParcsState>, { parcLimitropheId }: Parcs.DeleteParcLimitrophe) {
    return this.parcLimitropheService.parcLimitropheDelete( { id: parcLimitropheId }).pipe(
      tap( (res: number) => {
        patchState({
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  private timeToThisWeekDate(day: number, time: string) : Date
  {
    var datetime = new Date('1970-01-01T' + time);
    var d = moment().startOf('week').day(day).hour(datetime.getHours()).minute(datetime.getMinutes());
    return d.toDate();
  }

  private dateToTimeOnly(date: Date) : string {
    return date.getHours() + ':' + date.getMinutes() + ':00.0000000';
  }

  /*private duplicateOuvertureParc(dispatch, parcId: number, dow: number, dto: OuvertureParc, ) {
      let d: OuvertureParc;
      d.startDate = moment(dto.startDate).day(dow).toDate();
      d.endDate = moment(dto.endDate).day(dow).toDate();
      d.period = dto.period;
      d.id = dto.id
      d.weekdays = null;
      this.heuresOuvertureService.heureOuverturePost( { parcId: parcId, body: d});
  }*/

  @Action(Parcs.FetchHeuresOuvertureParc)
  fetchHeuresOuvertureParc(  { patchState }:StateContext<ParcsState>, { parcId }: Parcs.FetchHeuresOuvertureParc) {
    return this.heuresOuvertureService.heureOuvertureGetList( { parcId: parcId }).pipe(
      tap( (res: HeureOuvertureDto[]) => {

        let ouvertures = res.map( h => <OuvertureParc>{
            id: h.Id,
            startDate: this.timeToThisWeekDate(h.Jour, h.HeureDebut),
            endDate: this.timeToThisWeekDate(h.Jour, h.HeureFin),
            period: h.EstValableEnEte == null ? 'Always' : (h.EstValableEnEte ? 'Summer' : 'Winter' )
        });

        patchState({
          error: null,
          heuresOuverture: ouvertures
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.AddHeureOuvertureParc)
  addHeureOuvertureParc(  { patchState, dispatch }:StateContext<ParcsState>, { parcId, dto }: Parcs.AddHeureOuvertureParc) {
    // console.log('Call ADD OUVERTURE PARC');
    const dow = dto.startDate.getDay();
    const data = <HeureOuvertureDto> {
      Jour: dow,
      EstValableEnEte: dto.period == 'Always' ? null : (dto.period == 'Summer' ? true : false),
      HeureDebut: this.dateToTimeOnly(dto.startDate),
      HeureFin: this.dateToTimeOnly(dto.endDate),
      ParcId: parcId,
      Duplicate: dto.weekdays
    };

    return this.heuresOuvertureService.heureOuverturePost( { parcId: parcId, body: data }).pipe(
      tap(res => {
        patchState({
          error: null
        });
        dispatch(new Parcs.FetchHeuresOuvertureParc(parcId));
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.UpdateHeureOuvertureParc)
  updateHeureOuvertureParc(  { patchState, dispatch }:StateContext<ParcsState>, { parcId, dto }: Parcs.UpdateHeureOuvertureParc) {
    // console.log('Call UPDATE OUVERTURE PARC');
    const dow = dto.startDate.getDay();
    const data = <HeureOuvertureDto> {
      Jour: dow,
      EstValableEnEte: dto.period == 'Always' ? null : (dto.period == 'Summer' ? true : false),
      HeureDebut: this.dateToTimeOnly(dto.startDate),
      HeureFin: this.dateToTimeOnly(dto.endDate),
      ParcId: parcId,
      Id: dto.id,
      Duplicate: dto.weekdays
    };

    return this.heuresOuvertureService.heureOuverturePut( { id: dto.id, body: data }).pipe(
      tap(res => {
        patchState({
          error: null
        });
        dispatch(new Parcs.FetchHeuresOuvertureParc(parcId));
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.DeleteHeureOuvertureParc)
  deleteHeureOuvertureParc(  { patchState, dispatch }:StateContext<ParcsState>, { parcId, heureId }: Parcs.DeleteHeureOuvertureParc) {
    return this.heuresOuvertureService.heureOuvertureDelete( { id: heureId }).pipe(
      tap(res => {
        dispatch(new Parcs.FetchHeuresOuvertureParc(parcId));
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.ChangeAlertPublication)
  changeAlertPublication({ patchState, dispatch }: StateContext<ParcsState>, { alerteId, isPublished }: Parcs.ChangeAlertPublication) {
    return this.alertesService.alerteSetPublicationStatus({ id: alerteId, body: isPublished }).pipe(
      tap((res: AlerteDto) => {
        patchState({
          alerte: res,
          error: null
        });
      }),
      catchError((error: Error) => {
        patchState({
          alerte: null,
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.ApplyScheduleAllParcs)
  applyScheduleAllParcs({ patchState, dispatch }: StateContext<ParcsState>, { parcId }: Parcs.ApplyScheduleAllParcs) {
    return this.heuresOuvertureService.heureOuvertureSetToAllParcs({ parcId}).pipe(
      tap(res => {
        patchState({
          error: null
        });
      }),
      catchError((error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Parcs.DeletePhotoPda)
  deletePhotoPda({ patchState }: StateContext<ParcsState>, { photoPda }: Parcs.DeletePhotoPda) {
    return this.photosPdaService.photoPdaDelete({ id: photoPda.Id }).pipe(
      tap(res => {
        patchState({
          error: null
        });
      }),
      catchError((error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }
}
